import Cookies from "js-cookie";
const webUri = `${window.location.protocol}//${window.location.host}/`;
const authEnabled = !window.appSettings.auth.disableAuth;

const MOCK_USER = {
  sub: "mock-user-id",
  name: "Local Developer",
  email: "dev@localhost",
  roles: ["admin"],
};

export const redirectToLoginPage = () => {
  if (!authEnabled) {
    console.log("[LOCAL] Bypassing Cognito login");
    storeAuthInfo({
      id_token: createMockToken(),
      access_token: createMockToken(),
      refresh_token: "mock-refresh-token",
    });
    window.location.href = webUri;
    return;
  }

  const loginUrl = `https://${window.appSettings.auth.uri}/login?client_id=${window.appSettings.auth.clientId}&response_type=code&redirect_uri=${webUri}`;
  console.debug("Redirecting. Login URL is:", loginUrl);
  window.location.href = loginUrl;
};

export const getAccessToken = () => {
  return authEnabled
    ? window.localStorage.getItem("access_token")
    : "mock-access-token";
};

export const payloadFromToken = (token) => {
  if (!authEnabled) return MOCK_USER;

  const parts = token.split(".");
  if (parts.length !== 3) throw new Error("Invalid token");
  return JSON.parse(window.atob(parts[1]));
};

export function logOut() {
  if (window.appSettings.isOidc) {
    const origin = window.location.origin;
    const logOutPath = new URL("signout", origin); // Signout path can technically vary, it can be renamed in Terraform.
    window.location.href = logOutPath.href;
  } else {
    console.log(authEnabled ? "Logging out." : "[LOCAL] Clearing mock session");
    localStorage.removeItem("id_token");
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    if (authEnabled) redirectToLoginPage();
  }
}

// Parse authentication code returned from Cognito
export function parseAuthQueryString() {
  if (!authEnabled) return "mock-auth-code";
  const params = new URLSearchParams(window.location.search);
  const code = params.get("code");
  const error = params.get("error");
  if (error) {
    throw new Error(params.get("error_description") || "Invalid configuration");
  }
  return code;
}

async function authRequest(grant_type, data) {
  if (!authEnabled) {
    console.log("[LOCAL] Mocking auth request");
    return {
      id_token: createMockToken(),
      access_token: createMockToken(),
      refresh_token: "mock-refresh-token",
    };
  }
  console.debug("Doing", grant_type, " with", data);
  let body = new URLSearchParams();
  body.append("grant_type", grant_type);
  body.append("client_id", window.appSettings.auth.clientId);
  Object.keys(data).forEach((key) => {
    body.append(key, data[key]);
  });

  const url = `https://${window.appSettings.auth.uri}/oauth2/token`;

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body: body,
  });

  if (!response.ok) {
    console.debug(response);
    throw new Error("Bad response from auth endpoint");
  }

  return response.json();
}

export async function refreshToken() {
  if (!authEnabled) {
    console.log("[LOCAL] Mocking token refresh");
    return "mock-access-token";
  }
  // Remove old tokens
  window.localStorage.removeItem("id_token");
  window.localStorage.removeItem("access_token");
  // Get new token
  let data = await authRequest("refresh_token", {
    refresh_token: window.localStorage.getItem("refresh_token"),
  });
  console.debug("Tokens refreshed");

  storeAuthInfo(data);

  return data.access_token;
}

// Exchange our authentication code for a token, and then save it as a cookie.
export async function handleAuthentication(code) {
  if (!authEnabled) {
    console.log("[LOCAL] Mocking authentication");
    storeAuthInfo({
      id_token: createMockToken(),
      access_token: createMockToken(),
      refresh_token: "mock-refresh-token",
    });
    return;
  }

  console.debug("Exchanging code for token:", code);

  let authData;
  try {
    authData = await authRequest("authorization_code", {
      redirect_uri: webUri,
      code: code,
    });
  } catch (err) {
    console.error(err);
    return redirectToLoginPage();
  }

  storeAuthInfo(authData);

  // Remove code from URL
  const url = new URL(window.location);
  url.searchParams.delete("code");
  window.history.pushState({}, "", url);
}

const storeAuthInfo = (authData) => {
  if (authEnabled) {
    window.localStorage.setItem("id_token", authData.id_token);
    window.localStorage.setItem("access_token", authData.access_token);
    if (authData.refresh_token) {
      window.localStorage.setItem("refresh_token", authData.refresh_token);
    }
  } else {
    // Store mock tokens with different keys
    window.localStorage.setItem("mock_id_token", authData.id_token);
    window.localStorage.setItem("mock_access_token", authData.access_token);
  }
};

const createMockToken = () => {
  const header = btoa(JSON.stringify({ alg: "HS256", typ: "JWT" }));
  const payload = btoa(
    JSON.stringify({
      ...MOCK_USER,
      exp: Math.floor(Date.now() / 1000) + 3600, // 1 hour expiration
      iat: Math.floor(Date.now() / 1000),
    })
  );
  return `${header}.${payload}.mock-signature`;
};

export const getUsername = () => {
  if (window.appSettings.isOidc) {
    const cookieName = `CognitoIdentityServiceProvider.${window.appSettings.auth.clientId}.LastAuthUser`;
    const userName = Cookies.get(cookieName);
    return userName;
  } else {
    const token = getAccessToken();
    if (token) {
      const payload = payloadFromToken(token);
      const username = payload.username || payload.email || "User";
      return username;
    } else {
      return "User";
    }
  }
};
